import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import SuccessModal from "./SuccessModal";
import { useOutletContext } from "react-router-dom";
import { FaAngleLeft, FaCheck, FaTimes  } from "react-icons/fa";
import config from "../../../config/config";
import axios from "axios";

const BioComponent = ({ user }) => {
  const [bioText, setBioText] = useState("");
  const [isRightDivVisible, setRightDivVisibility] = useState(false);
  const [selection, setSelection] = useState("");
  const [isFemaleOnly, setIsFemaleOnly] = useState(false);
  const [isMaleOnly, setIsMaleOnly] = useState(false);
  const [isNonSmoker, setIsNonSmoker] = useState(false);
  const [isNoPets, setIsNoPets] = useState(false);
  const [isNoPreferences, setIsNoPreferences] = useState(false);
  const editor = useRef(null);
  const [validationError, setValidationError] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const baseURL = config.API_BASE_URL;
  const { isSmallScreen, handleBackClick } = useOutletContext()

  const lastErrorMessageRef = useRef(null);

  useEffect(() => {
    const fetchBioData = async () => {
      if (user && user.id) {
        try {
          const response = await axios.get(
            `${baseURL}/v1/worker-users/${user.id}`
          );
          const fetchedUserData = response.data;
          const bioData = fetchedUserData.bioData;

          if (bioData) {
            setBioText(bioData.bioText || "");
            setSelection(bioData.personality || "");

            setIsFemaleOnly(
              bioData.preferences?.includes("FemaleOnly") || false
            );
            setIsMaleOnly(bioData.preferences?.includes("MaleOnly") || false);
            setIsNonSmoker(bioData.preferences?.includes("NonSmoker") || false);
            setIsNoPets(bioData.preferences?.includes("NoPets") || false);
            setIsNoPreferences(
              bioData.preferences?.includes("NoPreferences") || false
            );
          }
        } catch (error) {
          console.error("Failed to fetch bio data:", error);
        }
      }
    };

    fetchBioData();
  }, [user, baseURL]);

  useEffect(() => {
    if (
      Object.keys(validationError).length > 0 &&
      lastErrorMessageRef.current
    ) {
      lastErrorMessageRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [validationError]);

  const handleTextChange = (content) => {
    const words = content.split(/\s+/);
    if (words.length <= 1500) {
      setBioText(content);
      if (content.length >= 200 && validationError.bioText) {
        setValidationError((prevErrors) => ({ ...prevErrors, bioText: "" }));
      }
    }
  };

  const getCharacterCount = () => {
    return bioText.length;
  };

  const handlePersonalityChange = (value) => {
    setSelection(value);
    if (validationError.selection) {
      setValidationError((prevErrors) => ({ ...prevErrors, selection: "" }));
    }
  };

  const handlePreferenceChange = (preference, value) => {
    switch (preference) {
      case "femaleOnly":
        setIsFemaleOnly(value);
        break;
      case "maleOnly":
        setIsMaleOnly(value);
        break;
      case "nonSmoker":
        setIsNonSmoker(value);
        break;
      case "noPets":
        setIsNoPets(value);
        break;
      case "noPreferences":
        setIsNoPreferences(value);
        break;
      default:
        break;
    }

    if (validationError.preferences) {
      setValidationError((prevErrors) => ({ ...prevErrors, preferences: "" }));
    }
  };

  const validateForm = () => {
    let tempErrors = {};
    tempErrors.bioText =
      bioText.length >= 200 ? "" : "Bio must be at least 200 characters *";
    tempErrors.selection = selection
      ? ""
      : "Personality selection is required *";

    if (!isNoPreferences) {
      tempErrors.preferences =
        isFemaleOnly || isMaleOnly || isNonSmoker || isNoPets
          ? ""
          : "At least one preference must be selected *";
    } else {
      tempErrors.preferences = "";
    }

    setValidationError(tempErrors);
    return Object.values(tempErrors).every((x) => x === "");
  };

  const handleSave = async () => {
    setSubmitAttempted(true);
    if (validateForm()) {
      const capturedBioData = {
        bioText: bioText,
        personality: selection,
        preferences: [
          ...(isFemaleOnly ? ["FemaleOnly"] : []),
          ...(isMaleOnly ? ["MaleOnly"] : []),
          ...(isNonSmoker ? ["NonSmoker"] : []),
          ...(isNoPets ? ["NoPets"] : []),
          ...(isNoPreferences ? ["NoPreferences"] : []),
        ],
      };

      console.log("Captured bioData:", capturedBioData);

      try {
        const response = await axios.patch(
          `${baseURL}/v1/worker-users/${user.id}`,
          { bioData: capturedBioData }
        );
        if (response.data) {
          console.log("Patch successful");
          setShowModal(true);
        }
      } catch (error) {
        console.error("Failed to update bio Data", error);
      }
    } else {
      console.error("Validation failed.", validationError);
    }
  };

  return (
    <div className="bio-custom-component-container">
      {showModal && <SuccessModal />}
      <div className="bio-indicative-left-div">
        <div className="profile-back-container">
          {isSmallScreen && (
            <div className="profile-back-arrow" onClick={handleBackClick}>
              <FaAngleLeft className="profile-back-icon" />
            </div>
          )}
          <h2>Your Bio</h2>
        </div>

        <p>
          Your bio serves as a brief introduction to potential clients, offering insights into your key skills, interests, and values to help them determine if you're a suitable match for their needs and whether to schedule a meet and greet. Consider what information would be most relevant to them, and optionally include your pronouns alongside a sentence about your personal enjoyments.
        </p>
        <div className="bio-indicative-right-div">
          <button
            className="bio-toggle-guidelines-btn"
            onClick={() => setRightDivVisibility(!isRightDivVisible)}
          >
            {isRightDivVisible
              ? "▲ Hide Bio Guidelines ▲ "
              : "▼ Show Bio Guidelines ▼ "}
          </button>
          <div className={isRightDivVisible ? "" : "active"}>
            <h3>Bio guidelines</h3>
            <ul className="guideline-list">
              <li className="guideline-item guideline-required">
                <FaCheck className="guideline-icon guideline-icon-success" />
                Include your key services or areas of expertise.
              </li>
              <li className="guideline-item guideline-required">
                <FaCheck className="guideline-icon guideline-icon-success" />
                Say how long you’ve been providing support.
              </li>
              <li className="guideline-item guideline-required">
                <FaCheck className="guideline-icon guideline-icon-success" />
                Add a sentence about your values and the things you enjoy.
              </li>
              <li className="guideline-item guideline-required">
                <FaCheck className="guideline-icon guideline-icon-success" />
                Smiling helps you look kind, friendly, and approachable.
              </li>
              <li className="guideline-item guideline-required">
                <FaCheck className="guideline-icon guideline-icon-success" />
                Check the spelling and grammar.
              </li>
              <li className="guideline-item guideline-required">
                <FaCheck className="guideline-icon guideline-icon-success" />
                Use inclusive language.
              </li>
              <li className="guideline-item guideline-not-required">
                <FaTimes className="guideline-icon guideline-icon-fail" />
                Only list qualified services (e.g., nursing) if you've provided proof of your qualifications.
              </li>
              <li className="guideline-item guideline-not-required">
                <FaTimes className="guideline-icon guideline-icon-fail" />
                Don’t add any personal information e.g. your phone number.
              </li>
              <li className="guideline-item guideline-not-required">
                <FaTimes className="guideline-icon guideline-icon-fail" />
                Don’t share private details of support services you’ve provided clients.
              </li>
            </ul>
          </div>

        </div>
      </div>

      <div className="current-bio-preferences">
        <div className="personality-bio-preference">
          <h3>
            Which of the following best describes your personality at work?
          </h3>
          <div className="about-bio-radio-group">
            <div className="about-bio-radio-option">
              <input
                type="radio"
                id="senseOfHumor"
                name="applyCheck"
                value="Sense of Humor"
                checked={selection === "Sense of Humor"}
                onChange={(e) => handlePersonalityChange(e.target.value)}
              />
              <label htmlFor="senseOfHumor">Sense of Humor</label>
            </div>
            <div className="about-bio-radio-option">
              <input
                type="radio"
                id="empathFocused"
                name="applyCheck"
                value="Empathy-focused"
                checked={selection === "Empathy-focused"}
                onChange={(e) => handlePersonalityChange(e.target.value)}
              />
              <label htmlFor="empathFocused">Empathy-focused</label>
            </div>
            <div className="about-bio-radio-option">
              <input
                type="radio"
                id="supportiveAndEncouraging"
                name="applyCheck"
                value="Supportive and Encouraging"
                checked={selection === "Supportive and Encouraging"}
                onChange={(e) => handlePersonalityChange(e.target.value)}
              />
              <label htmlFor="supportiveAndEncouraging">
                Supportive and Encouraging
              </label>
            </div>
          </div>
        </div>
        <div className="preferred-bio-preference">
          <h3>Who do you prefer to work with?</h3>
          <div className="checkbox-item">
            <input
              id="femaleOnlyCheckbox"
              type="checkbox"
              className="customized-checkbox"
              checked={isFemaleOnly}
              onChange={(e) =>
                handlePreferenceChange("femaleOnly", e.target.checked)
              }
            />
            <label htmlFor="femaleOnlyCheckbox">Female only</label>
          </div>

          <div className="checkbox-item">
            <input
              id="maleOnlyCheckbox"
              type="checkbox"
              className="customized-checkbox"
              checked={isMaleOnly}
              onChange={(e) =>
                handlePreferenceChange("maleOnly", e.target.checked)
              }
            />
            <label htmlFor="maleOnlyCheckbox">Male only</label>
          </div>

          <div className="checkbox-item">
            <input
              id="nonSmokerCheckbox"
              type="checkbox"
              className="customized-checkbox"
              checked={isNonSmoker}
              onChange={(e) =>
                handlePreferenceChange("nonSmoker", e.target.checked)
              }
            />
            <label htmlFor="nonSmokerCheckbox">Non-smoker</label>
          </div>

          <div className="checkbox-item">
            <input
              id="noPetsCheckbox"
              type="checkbox"
              className="customized-checkbox"
              checked={isNoPets}
              onChange={(e) =>
                handlePreferenceChange("noPets", e.target.checked)
              }
            />
            <label htmlFor="noPetsCheckbox">No pets</label>
          </div>

          <div className="checkbox-item">
            <input
              id="noPreferencesCheckbox"
              type="checkbox"
              className="customized-checkbox"
              checked={isNoPreferences}
              onChange={(e) =>
                handlePreferenceChange("noPreferences", e.target.checked)
              }
            />
            <label htmlFor="noPreferencesCheckbox">No preferences</label>
          </div>
        </div>
      </div>

      <div className="current-bio">
        <h3>Write your Bio below</h3>

        <ReactQuill
          value={bioText}
          onChange={handleTextChange}
          modules={{
            toolbar: [
              [{ header: "1" }, { header: "2" }, { font: [] }],
              [{ list: "ordered" }, { list: "bullet" }],
              ["bold", "italic", "underline"],
              [{ color: [] }, { background: [] }],
              ["clean"],
            ],
          }}
        />

        <div className="bio-indicator">
          {getCharacterCount() < 200 ? (
            <span className="warning">Minimum 200 characters required!</span>
          ) : (
            `Words: ${bioText.split(/\s+/).length}/1500`
          )}
        </div>
      </div>
      <div className="form-footer">
        <div className="error-messages-container">
          {Object.keys(validationError).map((key, index, arr) => {
            const isLast = index === arr.length - 1;
            return validationError[key] ? (
              <div
                key={key}
                ref={isLast ? lastErrorMessageRef : null}
                className="bio-error"
              >
                {validationError[key]}
              </div>
            ) : null;
          })}
        </div>
        <div className="bio-save">
          <button onClick={handleSave}>Submit for Review</button>
        </div>
      </div>
    </div>
  );
};

export default BioComponent;
